import type { BadgeProps } from '@/shared/components/ui/badge'
import type { AnchorStrategyModel } from '@/shared/generated'
import type { TFunction } from 'i18next'

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getAnchorsTypesChoices } from '@/entities/anchor'

import NumberField from '@/shared/components/data-grid/fields/Number/TableCell'
import { createSRTColumnHelper } from '@/shared/components/master-table'
import { useMasterReactTable } from '@/shared/components/master-table/hooks/useMasterReactTable'
import { Badge } from '@/shared/components/ui/badge'
import { LinkButton } from '@/shared/components/ui/link-button'

import OpenStrategyTrigger from '../ui/StrategiesTable/OpenStrategyTrigger'
import { StrategyActionsDropdownMenu } from '../ui/StrategiesTable/StrategyActionsDropdownMenu'

type BadgeVariant = BadgeProps['variant']

export const useStrategiesTable = (data: AnchorStrategyModel[]) => {
	const { t } = useTranslation()

	const [columnSizing, setColumnSizing] = useState({})
	const columns = getColumns(t)

	return useMasterReactTable({
		data,
		columns,
		state: {
			columnSizing,
			columnVisibility: {
				'srt-row-spacer': false,
			},
		},
		enableColumnResizing: true,
		onColumnSizingChange: setColumnSizing,
		enableColumnActions: false,
	})
}

const columnHelper = createSRTColumnHelper<AnchorStrategyModel>()
const getColumns = (t: TFunction) => [
	columnHelper.accessor('name', {
		header: t('field_schema_strategy_name', { ns: 'fields' }),
		description: t('field_schema_strategy_name', { ns: 'fields' }),
		size: 500,
		Cell: ({ cell, row }) => {
			const strategy = row.original

			return (
				<OpenStrategyTrigger strategy={strategy}>
					<LinkButton
						data-testid='anchor-strategy-name-link'
						variant='neutral'
						className='truncate'
					>
						<div className='flex items-center space-x-2'>{cell.getValue()}</div>
					</LinkButton>
				</OpenStrategyTrigger>
			)
		},
	}),
	columnHelper.accessor('optimization_group_ids', {
		size: 50,
		header: t('anchoring_management.strategy_table.ogs'),
		description: t('anchoring_management.strategy_table.ogs'),
		Cell: ({ cell }) => {
			const value = cell.getValue()?.length

			return <NumberField value={value} />
		},
	}),
	columnHelper.accessor('anchor_type', {
		header: t('anchoring_type'),
		size: 100,
		description: t('anchoring_type'),
		Cell: ({ cell }) => {
			const value = cell.getValue()
			const anchorChoices = getAnchorsTypesChoices(t).find(
				(choice) => choice.value === value
			)

			return (
				<Badge variant={anchorChoices?.variant as BadgeVariant}>
					{anchorChoices?.label || ''}
				</Badge>
			)
		},
	}),
	columnHelper.accessor('families_count', {
		size: 50,
		header: t('anchoring_management.strategy_table.families'),
		description: t('anchoring_management.strategy_table.families'),
		Cell: ({ cell }) => <NumberField value={cell.getValue()} />,
	}),
	columnHelper.accessor('products_count', {
		size: 50,
		header: t('app.products'),
		description: t('app.products'),
		Cell: ({ cell }) => <NumberField value={cell.getValue()} />,
	}),
	columnHelper.display({
		id: 'actions',
		maxSize: 25,
		header: '',
		description: '',
		Cell: ({ row }) => {
			const strategyId = row.original.id

			return (
				<div className='flex items-center justify-end space-x-1'>
					<StrategyActionsDropdownMenu id={strategyId} />
				</div>
			)
		},
	}),
]
